import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtTRY, fmtDate } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/Common";

const TYPE_LABEL = {
  collection: "Tahsilat",
  payment: "Ödeme",
  transfer_in: "Gelen Transfer",
  transfer_out: "Giden Transfer",
};

const TYPE_BADGE = {
  collection: "bg-emerald-50 text-emerald-700 border-emerald-200",
  payment: "bg-red-50 text-red-700 border-red-200",
  transfer_in: "bg-blue-50 text-blue-700 border-blue-200",
  transfer_out: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function CashMovementsDialog({ open, onOpenChange, account, accountLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !account) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/cash-accounts/${account}/movements`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, account]);

  const rows = data?.rows || [];
  const totals = useMemo(() => {
    if (!data) return { in: 0, out: 0, bal: 0 };
    return { in: data.total_in, out: data.total_out, bal: data.balance };
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="cash-movements-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{accountLabel} — Hareket Dökümü</DialogTitle>
          <DialogDescription className="text-xs">
            Tahsilatlar + Gelen Transferler − Ödemeler − Giden Transferler = Güncel Bakiye
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Yükleniyor…</div>
        ) : (rows ?? []).length === 0 ? (
          <EmptyState
            title="Henüz hareket yok"
            hint="Bu kasaya bağlı tahsilat, ödeme veya transfer henüz yapılmadı."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5 font-semibold">Tarih</th>
                  <th className="px-3 py-2.5 font-semibold">İşlem Tipi</th>
                  <th className="px-3 py-2.5 font-semibold">Kaynak</th>
                  <th className="px-3 py-2.5 font-semibold">Müşteri / Tedarikçi</th>
                  <th className="px-3 py-2.5 font-semibold">Açıklama / Not</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Giriş</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Çıkış</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Bakiye</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(rows ?? []).map((r, i) => (
                  <tr key={`${r.id}-${i}`} className="hover:bg-slate-50/60" data-testid={`cash-movement-row-${i}`}>
                    <td className="px-3 py-2.5 whitespace-nowrap">{r.date ? fmtDate(r.date) : "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${TYPE_BADGE[r.type] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {TYPE_LABEL[r.type] || r.type}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-800">{r.source_description || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-700">{r.party_name || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500 max-w-xs truncate">{r.notes || "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {r.in_amount > 0 ? (
                        <span className="text-emerald-600 font-medium">{fmtTRY(r.in_amount)}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {r.out_amount > 0 ? (
                        <span className="text-red-600 font-medium">{fmtTRY(r.out_amount)}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${r.balance < 0 ? "text-red-700" : "text-slate-900"}`}>
                      {fmtTRY(r.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 sticky bottom-0">
                <tr className="border-t-2 border-slate-200 font-semibold">
                  <td className="px-3 py-3" colSpan={5}>
                    <span className="text-[11px] uppercase tracking-wider text-slate-600">Toplam</span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-emerald-700" data-testid="cash-movements-total-in">
                    {fmtTRY(totals.in)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-red-700" data-testid="cash-movements-total-out">
                    {fmtTRY(totals.out)}
                  </td>
                  <td
                    className={`px-3 py-3 text-right tabular-nums ${totals.bal < 0 ? "text-red-700" : "text-slate-900"}`}
                    data-testid="cash-movements-final-balance"
                  >
                    {fmtTRY(totals.bal)}
                  </td>
                </tr>
                <tr className="text-xs text-slate-500">
                  <td className="px-3 pb-3" colSpan={8}>
                    Tahsilatlar + Gelen Transferler − Ödemeler − Giden Transferler = Güncel Kasa Bakiyesi
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
